import Button from "components/Button/Button";
import ButtonIcon from "components/ButtonIcon/ButtonIcon";
import Card from "components/Card/Card";
import {
  ControlledDateInput,
  ControlledInput,
} from "components/Card/ControlledInput";
import KaboodleToaster from "components/common/KaboodleToaster";
import ControlledSelect from "components/inputs/Select/ControlledSelect";
import MaskedInput from "components/inputs/TextInput/MaskedInput";
import KaboodleModal from "components/Modals/KaboodleModal";
import Tooltip from "components/Tooltip/Tooltip";
import SectionBreak from "components/SectionBreak";
import type { RequestToReopen } from "pages/liens/LienTypes";
import { Fragment, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  useCreateRequestToReopenMutation,
  useDeleteRequestToReopenMutation,
  useGetRequestToReopenQuery,
  usePatchRequestToReopenMutation,
} from "services/lien-api";
import { useConstants } from "utils/hooks";

import RequestToReopenModal from "./RequestToReopenModal";

type RequestToReopenSectionProps = {
  lienId: number;
  organizationId: number;
  accessToken: string;
};

type RequestToReopenFormValues = {
  [key: string]: string | number | null;
};

const RequestToReopenSection = ({
  lienId,
  organizationId,
}: RequestToReopenSectionProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState<number | null>(null);
  const [createRequestToReopen] = useCreateRequestToReopenMutation();
  const [patchRequestToReopen] = usePatchRequestToReopenMutation();
  const [deleteRequestToReopen] = useDeleteRequestToReopenMutation();
  const { data: requestToReopenRecords } = useGetRequestToReopenQuery({
    lienId,
  });
  const { constants } = useConstants();

  const decisionOptions =
    constants?.RequestToReopenDecision?.filter(
      (option) => option.value !== null,
    ) || [];

  const officeLevelOptions =
    constants?.OfficeLevelChoices?.filter((option) => option.value !== null) ||
    [];

  const { control, reset } = useForm<RequestToReopenFormValues>();

  useEffect(() => {
    if (requestToReopenRecords) {
      const defaultValues = requestToReopenRecords.results.reduce(
        (acc, record, index) => {
          acc[`request_to_reopen_date_${index}`] =
            record.request_to_reopen_date;
          acc[`lien_amount_${index}`] = record.lien_amount;
          acc[`request_to_reopen_amount_${index}`] =
            record.request_to_reopen_amount;
          acc[`request_to_reopen_savings_${index}`] =
            record.request_to_reopen_savings;
          acc[`request_to_reopen_decision_${index}`] =
            record.request_to_reopen_decision;
          acc[`office_level_${index}`] = record.office_level;
          return acc;
        },
        {} as RequestToReopenFormValues,
      );

      reset(defaultValues);
    }
  }, [requestToReopenRecords, reset]);

  const handleSubmit = async (data: RequestToReopen) => {
    try {
      await createRequestToReopen({
        data,
      }).unwrap();
    } catch (error) {
      console.error("Failed to create request to reopen:", error);
    }
  };

  const handleFieldChange = async (
    recordId: number,
    field: string,
    value: unknown,
  ) => {
    try {
      const finalValue =
        typeof value === "object" && value !== null && "value" in value
          ? (value as { value: string }).value
          : value;

      await patchRequestToReopen({
        id: recordId,
        data: {
          [field]: finalValue,
        },
      }).unwrap();
    } catch (error) {
      console.error("Failed to update request to reopen:", error);
    }
  };

  const handleDelete = async (id: number) => {
    setSelectedRecordId(id);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedRecordId) return;

    try {
      await deleteRequestToReopen({
        id: selectedRecordId,
      }).unwrap();
      setDeleteModalOpen(false);
      KaboodleToaster.success("Request to reopen deleted successfully");
    } catch (error) {
      console.error("Failed to delete request to reopen:", error);
      KaboodleToaster.error("Failed to delete request to reopen");
    }
  };

  return (
    <>
      <div className="header-primary flex jc-sb ai-c">
        Request to Reopen
        <div className="flex gap-sm jc-end">
          <Tooltip content="Add new request to reopen">
            <ButtonIcon
              kind="neutral"
              size="small"
              onClick={() => setIsModalOpen(true)}
              type="button"
            >
              add_circle
            </ButtonIcon>
          </Tooltip>
        </div>
      </div>

      {requestToReopenRecords?.results?.map((record, index) => (
        <Fragment key={record.id}>
          {index > 0 && <SectionBreak />}
          <div className="flex jc-se ai-c">
            <div className="text-sm text-gray-500">
              Request to Reopen #{index + 1}
            </div>
            <Tooltip content="Delete this request to reopen">
              <ButtonIcon
                kind="neutral"
                size="small"
                className="delete"
                onClick={() => handleDelete(record.id)}
                type="button"
              >
                delete
              </ButtonIcon>
            </Tooltip>
          </div>
          <ControlledDateInput
            control={control}
            name={`request_to_reopen_date_${index}`}
            label="Request Date"
            errors={{}}
            allowFutureDates
            onSave={async (value) => {
              await handleFieldChange(
                record.id,
                "request_to_reopen_date",
                value,
              );
            }}
          />
          <ControlledInput
            control={control}
            name={`lien_amount_${index}`}
            label="Lien Amount"
            errors={{}}
            componentType={MaskedInput}
            mask="dollar"
            onSave={async (value) => {
              await handleFieldChange(record.id, "lien_amount", value);
            }}
          />
          <ControlledInput
            control={control}
            name={`request_to_reopen_amount_${index}`}
            label="Request Amount"
            errors={{}}
            componentType={MaskedInput}
            mask="dollar"
            onSave={async (value) => {
              await handleFieldChange(
                record.id,
                "request_to_reopen_amount",
                value,
              );
            }}
          />
          <ControlledInput
            control={control}
            name={`request_to_reopen_savings_${index}`}
            label="Savings"
            errors={{}}
            componentType={MaskedInput}
            mask="dollar"
            onSave={async (value) => {
              await handleFieldChange(
                record.id,
                "request_to_reopen_savings",
                value,
              );
            }}
          />
          <ControlledSelect
            control={control}
            name={`request_to_reopen_decision_${index}`}
            label="Decision"
            errors={{}}
            options={decisionOptions}
            onSave={async (value) => {
              await handleFieldChange(
                record.id,
                "request_to_reopen_decision",
                value,
              );
            }}
          />
          <ControlledSelect
            control={control}
            name={`office_level_${index}`}
            label="Office Level"
            errors={{}}
            options={officeLevelOptions}
            onSave={async (value) => {
              await handleFieldChange(record.id, "office_level", value);
            }}
          />
        </Fragment>
      ))}

      <RequestToReopenModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        lienId={lienId}
        organizationId={organizationId}
      />

      <KaboodleModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        footer={
          <div className="flex jc-c">
            <Button onClick={handleConfirmDelete}>Delete</Button>
            <Button onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
          </div>
        }
      >
        <Card heading="Delete Request to Reopen">
          <p>Are you sure you want to delete this request to reopen?</p>
        </Card>
      </KaboodleModal>
    </>
  );
};

export default RequestToReopenSection;
